/* Owns translation: the flat dictionaries, `{var}` interpolation, the tr → en
   → key fallback chain and the persisted language choice. Dictionaries are flat
   `Record<string, string>` on purpose — key parity is then a set comparison a
   test can make, not a tree walk. */

import { LOCALES, type Locale } from '@sentinelac/shared'
import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { dict as en } from './en.js'
import { dict as tr } from './tr.js'

export type Dict = Record<string, string>
export type Lang = Locale

export const DICTS: Record<Lang, Dict> = { tr, en }
export const DEFAULT_LANG: Lang = 'tr'
/** Every key resolves through `en` before it degrades to the key itself. */
export const FALLBACK_LANG: Lang = 'en'

const STORAGE_KEY = 'sac.lang'

export type TFunction = (key: string, vars?: Record<string, string | number>) => string

function isLang(value: unknown): value is Lang {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
}

export function readStoredLang(): Lang | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isLang(stored) ? stored : null
  } catch {
    return null
  }
}

function writeStoredLang(lang: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang)
  } catch {
    /* A blocked storage must not stop the user switching language. */
  }
}

/** `UserDto.locale` → `localStorage['sac.lang']` → `'tr'` (PANEL.md §12). */
export function resolveInitialLang(userLocale?: Locale | null): Lang {
  if (isLang(userLocale)) return userLocale
  return readStoredLang() ?? DEFAULT_LANG
}

/**
 * Literal scan-and-replace, never `eval` and never HTML: every value that
 * reaches a dictionary string may have come from a player. A missing variable
 * renders as the empty string rather than leaving `{var}` on screen.
 */
export function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template.replace(/\{(\w+)\}/g, '')
  return template.replace(/\{(\w+)\}/g, (_match, name: string) => {
    const value = vars[name]
    return value === undefined || value === null ? '' : String(value)
  })
}

export function translate(lang: Lang, key: string, vars?: Record<string, string | number>): string {
  const primary = DICTS[lang][key]
  if (primary !== undefined) return interpolate(primary, vars)
  const fallback = DICTS[FALLBACK_LANG][key]
  if (fallback !== undefined) return interpolate(fallback, vars)
  if (import.meta.env.DEV) console.warn(`[i18n] missing key: ${key}`)
  return key
}

export interface LangContextValue {
  lang: Lang
  setLang: (lang: Lang) => void
  t: TFunction
}

const LangContext = createContext<LangContextValue | null>(null)

export interface LangProviderProps {
  children: ReactNode
  /** Test seam: pins the language without touching localStorage. */
  initialLang?: Lang
}

export function LangProvider({ children, initialLang }: LangProviderProps): ReactNode {
  const [lang, setLangState] = useState<Lang>(() => initialLang ?? resolveInitialLang())

  useEffect(() => {
    // `<html lang>` drives hyphenation, spell-check and screen-reader voice.
    if (typeof document !== 'undefined') document.documentElement.lang = lang
  }, [lang])

  const setLang = useCallback((next: Lang) => {
    writeStoredLang(next)
    setLangState(next)
  }, [])

  const value = useMemo<LangContextValue>(
    () => ({
      lang,
      setLang,
      t: (key, vars) => translate(lang, key, vars),
    }),
    [lang, setLang],
  )

  return createElement(LangContext.Provider, { value }, children)
}

export function useLang(): LangContextValue {
  const context = useContext(LangContext)
  if (!context) throw new Error('useLang must be used inside <LangProvider>')
  return context
}

export function useT(): TFunction {
  return useLang().t
}

export { en, tr }
